import { TenantContext } from '@/components';
import { SortDirection } from '@/components/Table/types';
import { usersRepo } from '@/constants/providers';
import { DataWithPagination } from '@/shared/types';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TenantUser, UserItem } from '../domain';
import { UserKey } from './types';

export const useGetUsers = (
  pageNumber?: number,
  filter?: string,
  sortBy?: string,
  sortOrder?: SortDirection,
) => {
  const { t } = useTranslation();
  const { currentSelectedTenant } = useContext(TenantContext);
  const query = useQuery<DataWithPagination<UserItem[]>>(
    [
      ...UserKey.LIST_PAGED({
        pageNumber,
        filter,
        sortBy,
        sortOrder,
      }),
      currentSelectedTenant?.id,
    ],
    () => usersRepo.getAllUsers(pageNumber, filter, sortBy, sortOrder),
    { cacheTime: 1000 * 60 * 5 },
  );

  useEffect(() => {
    if (query.isError) {
      toast.error(t('error_getting_users'));
    }
  }, [query.isError, t]);

  return query;
};

export const useGetTenantUsers = (
  data: {
    pageNumber?: number;
    filter?: string;
    sortBy?: string;
    sortOrder?: SortDirection;
    isAccountAdmin?: boolean;
  },
  options?: UseQueryOptions<DataWithPagination<TenantUser[]>>,
) => {
  const { currentSelectedTenant } = useContext(TenantContext);
  return useQuery<DataWithPagination<TenantUser[]>>(
    [
      ...UserKey.ADMIN_USERS_PAGED(
        {
          pageNumber: data.pageNumber,
          filter: data.filter,
          sortBy: data.sortBy,
          sortOrder: data.sortOrder,
        },
        data.isAccountAdmin,
      ),
      currentSelectedTenant?.id,
    ],
    () => usersRepo.getTenantUsers(data),
    { ...options, cacheTime: 1000 * 60 * 5 },
  );
};
